import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/useReveal";
import { Field, Input, Button } from "@/admin/ui";
import { userApi, USER_TOKEN_KEY, formatApiError } from "@/user/api";
import { TOKEN_KEY as ADMIN_TOKEN_KEY } from "@/admin/api";

const DEMO_ACCOUNTS = [
    { key: "admin", label: "Company Admin", cta: "Login as Company Admin", company: "EVOLVE Technologies", email: "admin@evolve.demo", password: "EvolveAdmin@123", accent: "from-violet-500 to-indigo-500" },
    { key: "ceo", label: "CEO", cta: "Login as CEO", company: "EVOLVE Technologies", email: "ceo@evolve.demo", password: "EvolveCEO@123", accent: "from-fuchsia-500 to-violet-500" },
    { key: "marketing", label: "Marketing", cta: "Login as Marketing Employee", company: "EVOLVE Technologies", email: "marketing@evolve.demo", password: "EvolveMarketing@123", accent: "from-indigo-500 to-sky-500" },
];

export default function UserLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showLogic, setShowLogic] = useState(false);
    const [highlight, setHighlight] = useState(false);

    function scrollToDemo() {
        setShowLogic(false);
        const el = document.getElementById("demo-credentials");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlight(true);
            setTimeout(() => setHighlight(false), 2200);
        }
    }

    function fillCreds(a) {
        setEmail(a.email);
        setPassword(a.password);
        setErr("");
        toast.success(`Filled ${a.label} credentials — click "Enter workspace"`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }
    function copyCreds(a) {
        const text = `${a.email} / ${a.password}`;
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => toast.success("Credentials copied")).catch(() => toast.success("Credentials copied"));
        else toast.success("Credentials copied");
    }

    async function onSubmit(e) {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const { data } = await userApi.post("/auth/portal-login", {
                email: email.trim(),
                password,
            });
            // Persist the token under the correct key, then do a full navigation
            // so the matching auth provider re-initialises from storage.
            if (data.actor === "admin") {
                localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
            } else {
                localStorage.setItem(USER_TOKEN_KEY, data.token);
                if (data.user?.must_change_password) {
                    window.location.assign("/change-password");
                    return;
                }
            }
            window.location.assign(data.redirect || "/app");
        } catch (e2) {
            setErr(formatApiError(e2));
            setBusy(false);
        }
    }

    return (
        <div
            data-testid="user-login-page"
            className="dot-grid min-h-screen w-full relative flex items-center justify-center px-6 py-16"
        >
            <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[560px] w-[820px] max-w-[95vw] rounded-full opacity-60 anim-pulse-glow"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(139,92,246,0.3), rgba(139,92,246,0.05) 60%, transparent 75%)",
                }}
            />

            <div className="relative w-full max-w-md">
                <div className="flex justify-center">
                    <div className="relative">
                        <div
                            className="pointer-events-none absolute inset-0 -m-4 rounded-full anim-pulse-glow"
                            style={{
                                background:
                                    "radial-gradient(closest-side, rgba(139,92,246,0.4), rgba(139,92,246,0.05) 60%, transparent 75%)",
                            }}
                        />
                        <img
                            src={LOGO_URL}
                            alt="CEO's GPT"
                            className="logo-blend relative h-24 w-24 object-contain anim-float-slow"
                            style={{
                                filter: "drop-shadow(0 20px 40px rgba(139,92,246,0.35))",
                            }}
                        />
                    </div>
                </div>

                <h1 className="mt-6 text-center font-display text-4xl sm:text-5xl font-light tracking-tight text-black">
                    Welcome{" "}
                    <span className="italic font-normal text-neutral-800">back</span>
                </h1>
                <p className="mt-3 text-center text-[14px] text-neutral-600">
                    One login for everyone. CEO’s GPT detects your role and opens the right dashboard.
                </p>

                <form
                    onSubmit={onSubmit}
                    className="mt-8 glass-strong rounded-3xl p-6 sm:p-8 space-y-4"
                >
                    <Field label="Email or Username">
                        <Input
                            data-testid="user-email-input"
                            required
                            autoComplete="username"
                            type="text"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                        />
                    </Field>
                    <Field label="Password">
                        <Input
                            data-testid="user-password-input"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                        />
                    </Field>

                    {err && (
                        <div
                            data-testid="user-login-error"
                            className="rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5"
                        >
                            {err}
                        </div>
                    )}

                    <Button
                        data-testid="user-login-submit"
                        type="submit"
                        size="lg"
                        disabled={busy}
                        className="w-full justify-center"
                    >
                        {busy ? "Signing in…" : "Enter workspace"}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Button>

                    <div className="flex items-center justify-between text-[12.5px] text-neutral-500 pt-2">
                        <button
                            type="button"
                            onClick={() =>
                                alert(
                                    "Password reset is available from your Company Admin or Platform Admin.",
                                )
                            }
                            data-testid="user-forgot-password"
                            className="hover:text-black transition-colors"
                        >
                            Forgot password?
                        </button>
                        <Link
                            to="/"
                            data-testid="user-back-to-landing"
                            className="inline-flex items-center gap-1 hover:text-black transition-colors"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Back to Landing
                        </Link>
                    </div>
                </form>

                <div className="mt-4 text-center text-[12px] text-neutral-500" data-testid="login-roles-hint">
                    Company Admin · CEO · All Departments — everyone signs in here.
                </div>

                {/* How it works trigger */}
                <button
                    type="button"
                    onClick={() => setShowLogic(true)}
                    data-testid="how-it-works-btn"
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[13px] font-medium text-violet-700 bg-white/70 backdrop-blur border border-violet-200 rounded-full py-2.5 hover:bg-violet-50 transition-colors"
                >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" /><path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                    How CEO’s GPT Works
                </button>

                {/* Demo Credentials for Jury */}
                <div
                    id="demo-credentials"
                    data-testid="demo-credentials-panel"
                    className={`mt-4 relative rounded-3xl border bg-white/70 backdrop-blur-xl p-5 sm:p-6 transition-all duration-500 ${highlight ? "border-violet-400 ring-4 ring-violet-200 scale-[1.01]" : "border-violet-200/70"}`}
                    style={{ boxShadow: "0 0 0 1px rgba(139,92,246,0.15), 0 30px 80px -40px rgba(139,92,246,0.6)" }}
                >
                    <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-60" style={{ background: "radial-gradient(120% 80% at 50% 0%, rgba(139,92,246,0.10), transparent 60%)" }} />
                    <div className="relative">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600">Demo Credentials for Jury</div>
                            <span data-testid="demo-mode-badge" className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase text-violet-700 bg-violet-50 border border-violet-100 rounded-full px-2.5 py-1">
                                <span className="h-1.5 w-1.5 rounded-full bg-violet-500 animate-pulse" /> Demo Mode
                            </span>
                        </div>
                        <p className="mt-2 text-[12.5px] text-neutral-600 leading-relaxed">
                            Use any demo login below to explore CEO’s GPT as Company Admin, CEO, or Marketing Employee.
                        </p>

                        <div className="mt-4 space-y-3">
                            {DEMO_ACCOUNTS.map((a) => (
                                <div key={a.key} data-testid={`demo-card-${a.key}`} className="rounded-2xl border border-black/[0.06] bg-white/80 p-3.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className={`h-7 w-7 rounded-lg bg-gradient-to-br ${a.accent} text-white grid place-items-center text-[11px] font-semibold shrink-0`}>{a.label.slice(0, 1)}</span>
                                            <div className="min-w-0">
                                                <div className="text-[12.5px] font-semibold text-black leading-tight truncate">EVOLVE {a.label}</div>
                                                <div className="text-[10.5px] text-neutral-400 truncate">{a.company}</div>
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => copyCreds(a)} data-testid={`demo-copy-${a.key}`}
                                            className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-full px-2.5 py-1.5 transition-colors">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" /><path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
                                            Copy
                                        </button>
                                    </div>
                                    <div className="mt-2.5 grid grid-cols-1 gap-1 text-[11.5px]">
                                        <div className="flex items-center gap-2"><span className="text-neutral-400 w-[68px] shrink-0">Email</span><span className="font-mono text-neutral-800 truncate">{a.email}</span></div>
                                        <div className="flex items-center gap-2"><span className="text-neutral-400 w-[68px] shrink-0">Password</span><span className="font-mono text-neutral-800 truncate">{a.password}</span></div>
                                    </div>
                                    <button type="button" onClick={() => fillCreds(a)} data-testid={`demo-login-${a.key}`}
                                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium bg-black text-white rounded-full py-2 hover:bg-neutral-800 transition-colors">
                                        {a.cta}
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2" data-testid="demo-warning">
                            These credentials are for challenge evaluation and demo testing only.
                        </div>
                    </div>
                </div>
            </div>

            {showLogic && <PlatformLogicModal onClose={() => setShowLogic(false)} onViewCreds={scrollToDemo} />}
        </div>
    );
}

const AGENTS = ["Sales Agent", "Finance Agent", "HR Agent", "Operations Agent", "Marketing Agent", "Customer Support Agent", "Procurement Agent", "Legal Agent", "Strategy Agent", "Meeting Agent"];
const FLOW = [
    "Master Admin creates a company.",
    "Master Admin creates the Company Admin.",
    "Company Admin logs in and manages the company workspace.",
    "Company Admin creates CEO and employee accounts.",
    "Employees log in through their assigned roles.",
    "Employees submit department data from their workspaces.",
    "The 10 department agents process this data.",
    "This processed business context is connected to the CEO LLM chat.",
    "The CEO asks questions and receives insights, summaries, money impact, risk alerts, decision cards & suggested actions.",
];

function PlatformLogicModal({ onClose, onViewCreds }) {
    return (
        <div className="fixed inset-0 z-[130] grid place-items-center p-4" data-testid="platform-logic-modal">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm anim-fade" onClick={onClose} />
            <div
                className="dot-grid relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-[28px] border border-violet-200 bg-white/95 backdrop-blur-2xl p-7 sm:p-9 anim-pop"
                style={{ boxShadow: "0 0 0 1px rgba(139,92,246,0.18), 0 50px 120px -30px rgba(139,92,246,0.6)" }}
            >
                <button type="button" onClick={onClose} data-testid="logic-close-x" aria-label="Close"
                    className="absolute right-4 top-4 h-9 w-9 grid place-items-center rounded-full text-neutral-400 hover:text-black hover:bg-black/[0.05] transition-colors">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                </button>

                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">Platform Logic & Demo Disclaimer</div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl font-light tracking-tight text-black">CEO’s GPT Platform Logic &amp; Demo Disclaimer</h2>
                <p className="mt-4 text-[14px] text-neutral-600 leading-relaxed">
                    CEO’s GPT is an AI business operating system where a CEO can converse with AI and take business decisions based on real company data.
                </p>

                <div className="mt-6 text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-400">Current Platform Flow</div>
                <ol className="mt-3 space-y-2.5">
                    {FLOW.map((f, i) => (
                        <li key={i} className="flex gap-3 items-start">
                            <span className="mt-0.5 h-5 w-5 shrink-0 grid place-items-center rounded-full bg-violet-600 text-white text-[10px] font-semibold">{i + 1}</span>
                            <span className="text-[13.5px] text-neutral-700 leading-relaxed">{f}</span>
                        </li>
                    ))}
                </ol>

                <div className="mt-5 rounded-2xl bg-violet-50/60 border border-violet-100 p-4">
                    <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-violet-600">10 Department Agents</div>
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {AGENTS.map((a) => <span key={a} className="text-[11px] font-medium text-violet-800 bg-white border border-violet-100 rounded-full px-2.5 py-1">{a}</span>)}
                    </div>
                </div>

                <div className="mt-6 text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-400">Demo Note</div>
                <p className="mt-2 text-[13.5px] text-neutral-700 leading-relaxed">
                    For the Emergent jury, demo credentials are provided below the login form. They let you test CEO’s GPT as <b>Company Admin</b>, <b>CEO</b>, and <b>Marketing Employee</b>. The demo company is <b>EVOLVE Technologies</b>, populated with mock data across all 10 departments so you can test the full business logic, CEO chat, department data flow, and decision-card experience.
                </p>

                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-[12px] text-amber-800 leading-relaxed" data-testid="logic-disclaimer">
                    This is a demo environment. Demo data is mock data — no real company data is used. Demo credentials are only for challenge evaluation. Real companies can later create their own workspace and users through the Master Admin and Company Admin flow.
                </div>

                <div className="mt-7 flex flex-col sm:flex-row items-center gap-3">
                    <button type="button" onClick={onClose} data-testid="logic-got-it"
                        className="w-full sm:w-auto flex-1 inline-flex items-center justify-center bg-black text-white text-sm font-medium rounded-full px-6 py-3 hover:bg-neutral-800 transition-colors">
                        Got it
                    </button>
                    <button type="button" onClick={onViewCreds} data-testid="logic-view-creds"
                        className="w-full sm:w-auto flex-1 inline-flex items-center justify-center gap-1.5 bg-white text-black border border-black/10 text-sm font-medium rounded-full px-6 py-3 hover:bg-violet-50 hover:border-violet-200 transition-colors">
                        View Demo Credentials
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                </div>
            </div>
        </div>
    );
}
